import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FloraTypography } from '@grupoboticario/flora-react';
import { ExclamationMarkHexagonIcon } from '@grupoboticario/flora-react-icons';
import {
  app,
  cart,
  CART_SHIPPING_MODEL_VIEW,
  IS_HYBRID_CART_ACTIVE,
  RootState,
  Utilities,
} from 'core';
import { Drawer, ShippingModelCta, TextBlock } from 'ui/components';
import { Container, Layout } from 'ui/components/primitives';
import { actions } from 'core/use-cases/shippingModel';
import { useGetFeatureFlag } from 'ui/hooks';
import {
  PickupStoreOptions,
  ShippingModelOptions,
  ShippingModelSearchBar,
} from 'ui/containers/ContentBuilder';
import { useShippingModel } from './hooks';
import { useUser } from 'core/use-cases/user/hooks';

const ShippingModelContainer = () => {
  const dispatch = useDispatch();
  const {
    isSuccess,
    data: { resellerId },
  } = useUser();

  const { value: isHybridCartActive, loading } = useGetFeatureFlag(
    IS_HYBRID_CART_ACTIVE,
    false,
    resellerId,
  );
  const cartStatus: RootState['cart']['fetch']['status'] = useSelector(
    (state: RootState) => state.cart.fetch.status,
  );
  const { handleClose, handleClick, handleOpen, shippingModel } = useShippingModel();

  const isShippingActive: boolean = app.hooks.useApp('isShippingActive');
  const step = cart.hooks.useCartPropertyByKey('step');
  const canSubmitShippingModel = cart.hooks.useCartPropertyByKey('canSubmitShippingModel');

  const titleDrawer =
    step === CART_SHIPPING_MODEL_VIEW.OPTIONS
      ? 'Receber ou retirar'
      : step === CART_SHIPPING_MODEL_VIEW.PRODUCT_NOT_FOUND
      ? 'Modelo de entrega'
      : 'Retirar pedido';

  const icon = step === CART_SHIPPING_MODEL_VIEW.OPTIONS ? 'Close' : 'ArrowBackward';

  const deliveriesAvailable = shippingModel?.availables?.find(
    (available) => available.code === shippingModel.default.code,
  )?.deliveries.length;

  useEffect(() => {
    if (loading) {
      return;
    }

    if (isHybridCartActive && isSuccess && cartStatus === Utilities.STATUS.SUCCESS) {
      dispatch(actions.availableDeliveryModesFetch.request());

      return;
    } else {
      dispatch(actions.toggleNewShippingModelActive(false));

      return;
    }
  }, [loading, isSuccess]);

  return shippingModel?.default?.delivery?.distributionCenterCode ? (
    <Drawer
      data-testid="shipping-modal-container"
      anchor="right"
      footer={
        <ShippingModelCta
          buttonLabel={
            deliveriesAvailable ? 'Selecionar este modelo' : 'Continuar para adição do produto'
          }
          disabled={!canSubmitShippingModel}
          onClick={handleClick}
        />
      }
      headerIcon={icon}
      onClose={handleClose}
      onOpen={handleOpen}
      open={isShippingActive}
      title={titleDrawer}
      size="default"
    >
      <>
        {deliveriesAvailable ? (
          <></>
        ) : (
          <>
            <Layout>
              <Container marginBottom="sp-2" marginTop="sp-4" size="full">
                <FloraTypography
                  as={'h6'}
                  color="dark-light"
                  fontSize="subtitle"
                  fontWeight="bold"
                  css={{ margin: 0 }}
                >
                  Você receberá o seu pedido no endereço cadastrado
                </FloraTypography>
              </Container>
            </Layout>
            <Layout>
              <Container marginBottom="sp-4" marginTop="sp-0" size="full">
                <TextBlock
                  text={'Caso precise alterar o endereço, entre em contato com o atendimento '}
                  textAlign={'left'}
                  variant={'exceptionsAuxiliar'}
                />
              </Container>
            </Layout>
          </>
        )}

        {CART_SHIPPING_MODEL_VIEW.OPTIONS === step && (
          <>
            <Container marginBottom="sp-0" marginTop="sp-4" size="full">
              <FloraTypography
                as={'h6'}
                color="dark-light"
                fontSize="subtitle"
                fontWeight="bold"
                css={{ margin: 0 }}
              >
                Deseja receber no endereço cadastrado ou retirar em um Espaço do Representante?
              </FloraTypography>
            </Container>

            <Container marginBottom="sp-4" marginTop="sp-2" size="full">
              <TextBlock
                text="Atenção: após começar um pedido, para mudar o modelo de entrega você precisa iniciar um pedido novo, finalizando o pedido atual ou excluindo todos os itens do carrinho."
                textAlign="left"
                variant="exceptionsAuxiliar"
                icon={<ExclamationMarkHexagonIcon size={16} />}
                iconColor="error"
              />
            </Container>

            <Layout alternateColor={true}>
              <Container marginBottom="sp-0" marginTop="sp-4" size="full">
                <ShippingModelOptions />
              </Container>
            </Layout>
          </>
        )}

        {CART_SHIPPING_MODEL_VIEW.SELECT_PICKUP_STORE === step && (
          <>
            <Container marginBottom="sp-4" marginTop="sp-4" size="full">
              <FloraTypography
                as={'h6'}
                color="dark-light"
                fontSize="subtitle"
                fontWeight="bold"
                css={{ margin: 0 }}
              >
                Busque ou selecione o espaço que deseja retirar seu pedido
              </FloraTypography>
            </Container>

            <Container marginBottom="sp-4" marginTop="sp-0" size="full">
              <ShippingModelSearchBar />
            </Container>

            <Layout alternateColor={true}>
              <Container marginBottom="sp-4" marginTop="sp-4" size="full">
                <PickupStoreOptions />
              </Container>
            </Layout>
          </>
        )}
      </>
    </Drawer>
  ) : (
    <></>
  );
};

export default ShippingModelContainer;
